import { GetDataDisplayPropertiesByConfig } from "@/shared/data-display-config/composable/data-display-config.ts";
import { DocumentationType, GroupedDocumentationItem } from "@/shared/documentation/documentation.types.ts";

/** `useContext` of a content node - besides the fixed fields it carries the dynamic display-config datafields. */
interface ContentNodeUseContext {
    custom_products?: string[] | null;
    // versionLabel is only selected as a workaround for topics, see generateGroupItemsQuery and the search query
    rootContentMap?: { teasers?: { title?: string | null }; versionLabel?: string | null } | null;
    [dataField: string]: unknown;
}

/**
 * The shape of a content node (`... on ContentMap` / `... on Topic`) as selected by the documentation queries.
 *
 * The node fragments are assembled at runtime from the display config (see `generateContentNodeFragments`),
 * so codegen cannot type them - this interface describes the fields the mappers rely on. Everything is
 * optional because which fields are selected depends on the display config and the content type.
 */
export interface ContentNode {
    __typename?: string;
    id?: string | null;
    contentId?: string | null;
    locale?: string | null;
    language?: string | null;
    mimeType?: string | null;
    version?: number | null;
    versionLabel?: string | null;
    teasers?: { title?: string | null } | null;
    useContext?: ContentNodeUseContext | null;
}

interface ContentNodeIdentity {
    type: DocumentationType;
    topicId: string | undefined;
    contentId: string;
    contentMapId: string;
}

/**
 * Derives the identity (type and the content/topic ids) of a content node. For a `Topic` the ids are
 * encoded in its id as `{topicId}@{contentMapId}`, a `ContentMap` carries them in `contentId`.
 */
export const mapContentNodeIdentity = (node: ContentNode): ContentNodeIdentity => {
    if (node.__typename !== "Topic") {
        return {
            type: "ContentMap",
            topicId: undefined,
            contentId: node.contentId ?? "",
            contentMapId: node.contentId ?? "",
        };
    }

    const [topicId, contentMapId] = node.id?.split("@") ?? [];

    return { type: "Topic", topicId, contentId: contentMapId ?? "", contentMapId: contentMapId ?? "" };
};

/**
 * Workaround: a topic has no version of its own - it shows the versionLabel of its root content map.
 * The node is copied (not mutated) so the display config reads the overridden value at its configured position.
 */
export const withRootVersionLabel = (node: ContentNode): ContentNode => {
    const rootVersionLabel = node.useContext?.rootContentMap?.versionLabel;
    return node.__typename === "Topic" && rootVersionLabel ? { ...node, versionLabel: rootVersionLabel } : node;
};

/**
 * Maps the fields every documentation view model shares, regardless of whether it originates from a
 * flat (`contents`) or a grouped (`contentsGrouped`) result.
 *
 * @param node - The raw content node of a hit.
 * @param cursor - The cursor of the hit the node belongs to.
 * @param language - The language of the node - resolved by the caller, as the queries select it differently.
 * @param getDataDisplayPropertiesByConfig - Maps a content node to the property items of the display config.
 */
export const mapContentNodeToDocumentationBase = (
    node: ContentNode,
    cursor: string,
    language: string,
    getDataDisplayPropertiesByConfig?: GetDataDisplayPropertiesByConfig
): GroupedDocumentationItem => ({
    id: node.id ?? "",
    cursor,
    ...mapContentNodeIdentity(node),
    title: node.teasers?.title ?? "",
    language,
    propertyItems: getDataDisplayPropertiesByConfig ? getDataDisplayPropertiesByConfig(withRootVersionLabel(node)) : [],
});
